import React, { useState, useEffect, useContext } from "react";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../style/settings.css";

function Settings() {
  const [authContext] = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const APP_ID = "bsf99";
  const API_BASE = process.env.NODE_ENV ===  "development" ? "http://localhost:8000/api/v1/api" : "https://api.bsf9090.com/api/v1/api" ;
//"https://api.bsf99.co/api/v1/api";
  const [betStakeSettings, setBetStakeSettings] = useState({
    minSessionAmt: "",
    maxSessionAmt: "",
    maxMatchOddsAmt: "",
    minMatchOddsAmt: "",
    maxTossOddsAmt: "",
    minTossOddsAmt: "",
  });

  const [urlSettings, setUrlSettings] = useState({
    socketUrl1: "",
    socketUrl2: "",
    socketUrl3: "",
    socketUrl4: "",
    socketUrl5: "",
    cricketMatchListUrl: "",
    tennisMatchListUrl: "",
    footballMatchListUrl: "",
    scoreApiUrl: "",
    backupIframeUrl: "",
    scoreApiUrl2: "",
    scoreApiUrl3: "",
  });

  const [permissionSettings, setPermissionSettings] = useState({
    matchBetDelaySeconds: "",
    sessionBetDelaySeconds: "",
  });

  useEffect(() => {
    fetchSettings();

    // Prevent scroll wheel from changing number input values
    const handleWheel = (e) => {
      if (e.target.type === "number") {
        e.target.blur();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/settings/${APP_ID}`, {
        headers: {
          Authorization: `Bearer ${authContext.token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        const settings = response.data.data;
        // Map API response to component state
        setBetStakeSettings({
          minSessionAmt: Number(settings.min_session_amt) || "",
          maxSessionAmt: Number(settings.max_session_amt) || "",
          maxMatchOddsAmt: Number(settings.max_match_odds_amt) || "",
          minMatchOddsAmt: Number(settings.min_match_odds_amt) || "",
          maxTossOddsAmt: Number(settings.max_toss_odds_amt) || "",
          minTossOddsAmt: Number(settings.min_toss_odds_amt) || "",
        });

        setUrlSettings({
          socketUrl1: settings.socket_url1 || "",
          socketUrl2: settings.socket_url2 || "",
          socketUrl3: settings.socket_url3 || "",
          socketUrl4: settings.socket_url4 || "",
          socketUrl5: settings.socket_url5 || "",
          cricketMatchListUrl: settings.cricket_match_list_url || "",
          tennisMatchListUrl: settings.tennis_match_list_url || "",
          footballMatchListUrl: settings.football_match_list_url || "",
          scoreApiUrl: settings.score_api_url || "",
          backupIframeUrl: settings.backup_iframe_url || "",
          scoreApiUrl2: settings.score_api_url2 || "",
          scoreApiUrl3: settings.score_api_url3 || "",
        });

        setPermissionSettings({
          matchBetDelaySeconds: Number(settings.match_bet_delay_seconds) || "",
          sessionBetDelaySeconds: Number(settings.session_bet_delay_seconds) || "",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleBetStakeChange = (field, value) => {
    // Prevent negative values
    if (value < 0) return;

    setBetStakeSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUrlChange = (field, value) => {
    setUrlSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePermissionChange = (field, value) => {
    // Prevent negative values
    if (value < 0) return;

    setPermissionSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      // Prepare data for API (convert camelCase to snake_case)
      const apiData = {
        min_session_amt: parseFloat(betStakeSettings.minSessionAmt) || null,
        max_session_amt: parseFloat(betStakeSettings.maxSessionAmt) || null,
        max_match_odds_amt:
          parseFloat(betStakeSettings.maxMatchOddsAmt) || null,
        min_match_odds_amt:
          parseFloat(betStakeSettings.minMatchOddsAmt) || null,
        max_toss_odds_amt: parseFloat(betStakeSettings.maxTossOddsAmt) || null,
        min_toss_odds_amt: parseFloat(betStakeSettings.minTossOddsAmt) || null,
        socket_url1: urlSettings.socketUrl1 || null,
        socket_url2: urlSettings.socketUrl2 || null,
        socket_url3: urlSettings.socketUrl3 || null,
        socket_url4: urlSettings.socketUrl4 || null,
        socket_url5: urlSettings.socketUrl5 || null,
        cricket_match_list_url: urlSettings.cricketMatchListUrl || null,
        tennis_match_list_url: urlSettings.tennisMatchListUrl || null,
        football_match_list_url: urlSettings.footballMatchListUrl || null,
        score_api_url: urlSettings.scoreApiUrl || null,
        backup_iframe_url: urlSettings.backupIframeUrl || null,
        score_api_url2: urlSettings.scoreApiUrl2 || null,
        score_api_url3: urlSettings.scoreApiUrl3 || null,
        match_bet_delay_seconds: parseInt(permissionSettings.matchBetDelaySeconds) || null,
        session_bet_delay_seconds: parseInt(permissionSettings.sessionBetDelaySeconds) || null,
      };

      const response = await axios.put(
        `${API_BASE}/settings/${APP_ID}`,
        apiData,
        {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Settings saved successfully!");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Update Setting</h1>
      </div>

      <div className="settings-content">
        {/* Bet Stake Settings Section */}
        <div className="settings-section">
          <h2>Bet Stake Setting</h2>
          <div className="settings-grid">
            <div className="setting-field">
              <label htmlFor="minSessionAmt">Min session amt :</label>
              <input
                type="number"
                min="0"
                id="minSessionAmt"
                value={betStakeSettings.minSessionAmt}
                onChange={(e) =>
                  handleBetStakeChange("minSessionAmt", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="maxSessionAmt">Max session amt :</label>
              <input
                type="number"
                min="0"
                id="maxSessionAmt"
                value={betStakeSettings.maxSessionAmt}
                onChange={(e) =>
                  handleBetStakeChange("maxSessionAmt", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="minMatchOddsAmt">Min matchOdds amt:</label>
              <input
                type="number"
                min="0"
                id="minMatchOddsAmt"
                value={betStakeSettings.minMatchOddsAmt}
                onChange={(e) =>
                  handleBetStakeChange("minMatchOddsAmt", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="maxMatchOddsAmt">Max matchOdds amt :</label>
              <input
                type="number"
                min="0"
                id="maxMatchOddsAmt"
                value={betStakeSettings.maxMatchOddsAmt}
                onChange={(e) =>
                  handleBetStakeChange("maxMatchOddsAmt", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="minTossOddsAmt">Min tossOdds amt :</label>
              <input
                type="number"
                min="0"
                id="minTossOddsAmt"
                value={betStakeSettings.minTossOddsAmt}
                onChange={(e) =>
                  handleBetStakeChange("minTossOddsAmt", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="maxTossOddsAmt">Max tossOdds amt:</label>
              <input
                type="number"
                min="0"
                id="maxTossOddsAmt"
                value={betStakeSettings.maxTossOddsAmt}
                onChange={(e) =>
                  handleBetStakeChange("maxTossOddsAmt", e.target.value)
                }
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Imp Url Settings Section */}
        <div className="settings-section">
          <h2>Imp Url Setting</h2>
          <div className="settings-grid">
            <div className="setting-field">
              <label htmlFor="socketUrl1">Socket url 1 :</label>
              <input
                type="url"
                id="socketUrl1"
                value={urlSettings.socketUrl1}
                onChange={(e) => handleUrlChange("socketUrl1", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="socketUrl2">Socket url 2 :</label>
              <input
                type="url"
                id="socketUrl2"
                value={urlSettings.socketUrl2}
                onChange={(e) => handleUrlChange("socketUrl2", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="socketUrl3">Socket url 3 :</label>
              <input
                type="url"
                id="socketUrl3"
                value={urlSettings.socketUrl3}
                onChange={(e) => handleUrlChange("socketUrl3", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="socketUrl4">Socket url 4 :</label>
              <input
                type="url"
                id="socketUrl4"
                value={urlSettings.socketUrl4}
                onChange={(e) => handleUrlChange("socketUrl4", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="socketUrl5">Socket url 5 :</label>
              <input
                type="url"
                id="socketUrl5"
                value={urlSettings.socketUrl5}
                onChange={(e) => handleUrlChange("socketUrl5", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="cricketMatchListUrl">
                Cricket matchList url :
              </label>
              <input
                type="url"
                id="cricketMatchListUrl"
                value={urlSettings.cricketMatchListUrl}
                onChange={(e) =>
                  handleUrlChange("cricketMatchListUrl", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="tennisMatchListUrl">Tennis matchList url :</label>
              <input
                type="url"
                id="tennisMatchListUrl"
                value={urlSettings.tennisMatchListUrl}
                onChange={(e) =>
                  handleUrlChange("tennisMatchListUrl", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="footballMatchListUrl">
                Football matchList url :
              </label>
              <input
                type="url"
                id="footballMatchListUrl"
                value={urlSettings.footballMatchListUrl}
                onChange={(e) =>
                  handleUrlChange("footballMatchListUrl", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="scoreApiUrl">Score api url :</label>
              <input
                type="url"
                id="scoreApiUrl"
                value={urlSettings.scoreApiUrl}
                onChange={(e) => handleUrlChange("scoreApiUrl", e.target.value)}
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="backupIframeUrl">Backup iframe url :</label>
              <input
                type="url"
                id="backupIframeUrl"
                value={urlSettings.backupIframeUrl}
                onChange={(e) =>
                  handleUrlChange("backupIframeUrl", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="scoreApiUrl2">Score api url 2 :</label>
              <input
                type="url"
                id="scoreApiUrl2"
                value={urlSettings.scoreApiUrl2}
                onChange={(e) =>
                  handleUrlChange("scoreApiUrl2", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="scoreApiUrl3">Score api url 3 :</label>
              <input
                type="url"
                id="scoreApiUrl3"
                value={urlSettings.scoreApiUrl3}
                onChange={(e) =>
                  handleUrlChange("scoreApiUrl3", e.target.value)
                }
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Other Imp Permission Settings Section */}
        <div className="settings-section">
          <h2>Other Imp Permission Setting</h2>
          <div className="settings-grid">
            <div className="setting-field">
              <label htmlFor="matchBetDelaySeconds">Match bet delay in seconds :</label>
              <input
                type="number"
                min="0"
                id="matchBetDelaySeconds"
                value={permissionSettings.matchBetDelaySeconds}
                onChange={(e) =>
                  handlePermissionChange("matchBetDelaySeconds", e.target.value)
                }
                placeholder=""
              />
            </div>

            <div className="setting-field">
              <label htmlFor="sessionBetDelaySeconds">Session bet delay in seconds :</label>
              <input
                type="number"
                min="0"
                id="sessionBetDelaySeconds"
                value={permissionSettings.sessionBetDelaySeconds}
                onChange={(e) =>
                  handlePermissionChange("sessionBetDelaySeconds", e.target.value)
                }
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-footer">
          <button
            className="save-btn"
            onClick={handleSaveSettings}
            disabled={loading}
          >
            <SaveIcon />
            {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default Settings;
